import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "@/lib/supabase";
import type { UserRole } from "@/lib/auth";

export interface AuthUserProfile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  roles: UserRole[];
  activeRole: UserRole | null;
}

export function useAuthSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      setLoading(false);
      if (event === "SIGNED_OUT") {
        queryClient.removeQueries({ queryKey: ["auth-user-profile"] });
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") {
        queryClient.invalidateQueries({ queryKey: ["auth-user-profile"] });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [queryClient]);

  return { session, loading };
}

async function fetchAuthUserProfile(userId: string): Promise<AuthUserProfile | null> {
  const [userRes, rolesRes] = await Promise.all([
    supabase
      .from("users")
      .select("id, email, full_name, avatar_url, active_role")
      .eq("id", userId)
      .maybeSingle(),
    supabase.from("user_roles").select("role").eq("user_id", userId),
  ]);
  if (userRes.error) throw new Error(userRes.error.message);
  if (rolesRes.error) throw new Error(rolesRes.error.message);
  if (!userRes.data) return null;
  const rows = (rolesRes.data ?? []) as Array<{ role: UserRole }>;
  return {
    id: userRes.data.id as string,
    email: userRes.data.email as string,
    fullName: (userRes.data.full_name as string) ?? "",
    avatarUrl: (userRes.data.avatar_url as string | null) ?? null,
    activeRole: (userRes.data.active_role as UserRole | null) ?? null,
    roles: rows.map((r) => r.role),
  };
}

export function useAuthUser(): { user: AuthUserProfile | null; loading: boolean } {
  const { session, loading: sessionLoading } = useAuthSession();
  const userId = session?.user?.id;

  const { data, isLoading } = useQuery({
    queryKey: ["auth-user-profile", userId],
    queryFn: () => fetchAuthUserProfile(userId as string),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  return {
    user: data ?? null,
    loading: sessionLoading || (Boolean(userId) && isLoading),
  };
}

export function hasRole(user: AuthUserProfile | null, role: UserRole): boolean {
  return Boolean(user && user.roles.includes(role));
}
